import React, { useEffect, useState } from 'react';

const API = 'https://v-room.app/api';

const CATEGORY_LABELS = {
  news:   { label: 'AKTUALNOŚCI', color: '#2196f3' },
  update: { label: 'AKTUALIZACJA', color: '#9c27b0' },
  beta:   { label: 'BETA',        color: '#ff4d00' },
  event:  { label: 'EVENT',       color: '#f5c518' },
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)        return 'przed chwilą';
  if (diff < 3600)      return `${Math.floor(diff / 60)} min. temu`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)} godz. temu`;
  if (diff < 2592000)   return `${Math.floor(diff / 86400)} dni temu`;
  return new Date(dateStr).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Modal szczegółów ──────────────────────────────────────────────────────────
function AnnouncementModal({ item, onClose }) {
  const cat = CATEGORY_LABELS[item.category] ?? CATEGORY_LABELS.news;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="ann-modal-overlay" onClick={onClose}>
      <div className="ann-modal" onClick={e => e.stopPropagation()}>
        <button className="ann-modal-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>

        {item.coverImage && (
          <img src={item.coverImage} alt={item.title} className="ann-modal-cover" />
        )}

        <div className="ann-modal-body">
          <div className="ann-modal-meta">
            <span className="ann-cat-badge" style={{ background: cat.color + '20', color: cat.color, borderColor: cat.color + '40' }}>
              {cat.label}
            </span>
            {item.pinned && <span className="ann-pinned-badge">📌 PRZYPIĘTE</span>}
            <span className="ann-modal-date">{timeAgo(item.createdAt)}</span>
          </div>

          <h2 className="ann-modal-title">{item.title}</h2>
          <div className="ann-modal-content">
            {item.content.split('\n').map((line, i) =>
              line.trim() === ''
                ? <br key={i} />
                : <p key={i}>{line}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Karta posta ───────────────────────────────────────────────────────────────
function AnnouncementCard({ item, onClick }) {
  const cat = CATEGORY_LABELS[item.category] ?? CATEGORY_LABELS.news;
  const excerpt = item.excerpt || item.content.slice(0, 120) + (item.content.length > 120 ? '...' : '');

  return (
    <div className={`ann-card ${item.pinned ? 'ann-card-pinned' : ''}`} onClick={() => onClick(item)}>
      {item.coverImage && (
        <div className="ann-card-img-wrap">
          <img src={item.coverImage} alt={item.title} className="ann-card-img" />
          {item.pinned && <div className="ann-card-pin">📌</div>}
        </div>
      )}
      <div className="ann-card-body">
        <div className="ann-card-meta">
          <span className="ann-cat-badge" style={{ background: cat.color + '20', color: cat.color, borderColor: cat.color + '40' }}>
            {cat.label}
          </span>
          <span className="ann-card-date">{timeAgo(item.createdAt)}</span>
        </div>
        <h3 className="ann-card-title">{item.title}</h3>
        <p className="ann-card-excerpt">{excerpt}</p>
        <div className="ann-card-read">
          Czytaj więcej <span className="material-icons">arrow_forward</span>
        </div>
      </div>
    </div>
  );
}

// ── Główna sekcja ─────────────────────────────────────────────────────────────
export default function AnnouncementsSection() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter,   setFilter]   = useState('all');

  useEffect(() => {
    fetch(`${API}/announcements`)
      .then(r => r.json())
      .then(data => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const categories = ['all', ...new Set(items.map(i => i.category))];

  const filtered = filter === 'all'
    ? items
    : items.filter(i => i.category === filter);

  return (
    <section id="news" className="ann-section">
      <div className="section-title">
        <h2>AKTUALNOŚCI <span>& NEWSY</span></h2>
        <p>Bądź na bieżąco z rozwojem VROOM</p>
      </div>

      {/* filtry */}
      {categories.length > 2 && (
        <div className="ann-filters">
          {categories.map(c => {
            const cat = CATEGORY_LABELS[c];
            return (
              <button
                key={c}
                className={`ann-filter-btn ${filter === c ? 'active' : ''}`}
                style={filter === c && cat ? { borderColor: cat.color, color: cat.color } : {}}
                onClick={() => setFilter(c)}
              >
                {c === 'all' ? 'WSZYSTKIE' : (cat?.label ?? c.toUpperCase())}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="ann-loading">
          <div className="profile-spinner"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ann-empty">
          <span className="material-icons">article</span>
          <p>Brak aktualności</p>
        </div>
      ) : (
        <div className="ann-grid">
          {filtered.map(item => (
            <AnnouncementCard key={item.id} item={item} onClick={setSelected} />
          ))}
        </div>
      )}

      {selected && (
        <AnnouncementModal item={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}